/**
 * The rate-card grid, shared by the slab editor and (via the floor hints) the merchant form.
 *
 * Same four blocks and the same `config` shape the merchant pricing forms already save, so a slab's
 * card can be copied onto a merchant untouched and the price calculator never sees a second format.
 *
 * `floor` is what makes this worth its own component: when a floor is supplied, every box shows the
 * lowest price allowed and turns red the moment it is undercut — so somebody finds out while they
 * type, not after they press Save and the server refuses it.
 */
export interface RateRow {
  upto?: string | number;
  city?: number; state?: number; metro?: number; zone?: number; roi?: number; sd?: number;
  codAmount?: number; codPercent?: number;
  sameAsAbove?: boolean; sameAsForward?: boolean;
}

export interface RateConfig {
  forwardBasic: RateRow;
  forwardAdditional: RateRow;
  rtoBasic: RateRow;
  rtoAdditional: RateRow;
  /**
   * DTO — reverse pickup: collected from the customer and returned to the merchant on a NEW
   * waybill, because they asked for it. Not the same thing as RTO, which is the same consignment
   * coming back after delivery failed, so it does not share the RTO rate. Priced across the same
   * zones because a reverse pickup is a full leg — a single flat figure cannot price Chennai and
   * Guwahati the same.
   *
   * Left BLANK by default and on every existing plan: an unpriced reverse pickup bills nothing,
   * which is the honest outcome. Defaulting it to the forward rate would start charging for a
   * service nobody has agreed a price for.
   */
  dtoBasic: RateRow;
  dtoAdditional: RateRow;
}

export const blankRateConfig: RateConfig = {
  forwardBasic: {}, forwardAdditional: {}, rtoBasic: {}, rtoAdditional: {},
  dtoBasic: {}, dtoAdditional: {},
};

/** Does this card carry a single real price? Mirrors the server's own guard. */
export function hasAnyPrice(cfg: any): boolean {
  const walk = (o: any): boolean => {
    if (!o || typeof o !== 'object') return false;
    return Object.entries(o).some(([k, v]) => {
      if (k === 'upto' || typeof v === 'boolean') return false;
      if (v && typeof v === 'object') return walk(v);
      const n = Number(v);
      return Number.isFinite(n) && n > 1;
    });
  };
  return walk(cfg);
}

const FIELDS: Array<{ key: keyof RateRow; label: string }> = [
  { key: 'city', label: 'City (₹)' },
  { key: 'state', label: 'State (₹)' },
  { key: 'metro', label: 'Metro (₹)' },
  { key: 'zone', label: 'Zone (₹)' },
  { key: 'roi', label: 'ROI (₹)' },
  { key: 'sd', label: 'SD (₹)' },
];

export function RateCardFields({
  cfg, onChange, floor, floorSource,
}: {
  cfg: RateConfig;
  onChange: (next: RateConfig) => void;
  /** Dotted path → lowest allowed value, e.g. `{"forwardBasic.city": 42}`. */
  floor?: Record<string, number>;
  /** Dotted path → the plan that floor came from, for the hint text. */
  floorSource?: Record<string, string>;
}) {
  const update = (section: keyof RateConfig, patch: Partial<RateRow>) =>
    onChange({ ...cfg, [section]: { ...(cfg[section] as RateRow), ...patch } });

  return (
    <div className="space-y-3">
      <Block title="Forward — Basic" section="forwardBasic" row={cfg.forwardBasic} update={update} floor={floor} floorSource={floorSource} withCod />
      <Block title="Forward — Additional" section="forwardAdditional" row={cfg.forwardAdditional} update={update} floor={floor} floorSource={floorSource} sameAs="above" />
      <Block title="RTO — Basic" section="rtoBasic" row={cfg.rtoBasic} update={update} floor={floor} floorSource={floorSource} sameAs="forward" />
      <Block title="RTO — Additional" section="rtoAdditional" row={cfg.rtoAdditional} update={update} floor={floor} floorSource={floorSource} sameAs="above" />
      <Block title="DTO (Reverse Pickup) — Basic" section="dtoBasic" row={cfg.dtoBasic ?? {}} update={update} floor={floor} floorSource={floorSource} sameAs="forward" />
      <Block title="DTO (Reverse Pickup) — Additional" section="dtoAdditional" row={cfg.dtoAdditional ?? {}} update={update} floor={floor} floorSource={floorSource} sameAs="above" />
    </div>
  );
}

function Block({
  title, section, row, update, floor, floorSource, sameAs, withCod,
}: {
  title: string;
  section: keyof RateConfig;
  row: RateRow;
  update: (s: keyof RateConfig, p: Partial<RateRow>) => void;
  floor?: Record<string, number>;
  floorSource?: Record<string, string>;
  sameAs?: 'above' | 'forward';
  withCod?: boolean;
}) {
  const sameKey = sameAs === 'forward' ? 'sameAsForward' : 'sameAsAbove';
  const sameLabel = sameAs === 'forward' ? 'Same as Forward' : 'Same as Above';
  const fields = withCod
    ? [...FIELDS, { key: 'codAmount' as const, label: 'COD Amount (₹)' }, { key: 'codPercent' as const, label: 'COD %' }]
    : FIELDS;

  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/40">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold text-slate-900">{title}</h4>
        {sameAs && (
          <label className="flex items-center gap-2 text-xs text-slate-700">
            <input type="checkbox" checked={!!(row as any)[sameKey]}
              onChange={(e) => update(section, { [sameKey]: e.target.checked } as Partial<RateRow>)} className="rounded" />
            {sameLabel}
          </label>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
        <Field label="Upto (kg)" value={row.upto ?? ''} onChange={(v) => update(section, { upto: v })} />
        {fields.map((f) => {
          const path = `${section}.${String(f.key)}`;
          const min = floor?.[path];
          const raw = (row as any)[f.key];
          const under = min !== undefined && raw !== '' && raw !== undefined && Number(raw) < min - 0.0001;
          return (
            <Field
              key={path}
              label={f.label}
              value={raw ?? ''}
              onChange={(v) => update(section, { [f.key]: v === '' ? undefined : Number(v) } as Partial<RateRow>)}
              hint={min !== undefined ? `min ${min}` : undefined}
              hintTitle={min !== undefined && floorSource?.[path] ? `Lowest allowed is ${min}, from the "${floorSource[path]}" plan` : undefined}
              error={under}
            />
          );
        })}
      </div>
    </div>
  );
}

function Field({
  label, value, onChange, hint, hintTitle, error,
}: {
  label: string;
  value: any;
  onChange: (v: string) => void;
  hint?: string;
  hintTitle?: string;
  error?: boolean;
}) {
  return (
    <div>
      <label className="block text-[11px] text-slate-500 mb-0.5 truncate" title={label}>{label}</label>
      <input
        type="number" step="0.01" value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full px-2 py-1.5 border rounded text-sm ${error ? 'border-rose-400 bg-rose-50 text-rose-700' : 'border-slate-300'}`}
      />
      {hint && (
        <div className={`text-[10px] mt-0.5 ${error ? 'text-rose-600 font-medium' : 'text-slate-400'}`} title={hintTitle}>
          {error ? `below ${hint.replace('min ', '')}` : hint}
        </div>
      )}
    </div>
  );
}
